/**
 * "Show me on my plan" — the bridge from a chapter into the real screen.
 * Navigation only (URL params), never a write. Permission-aware: a viewer
 * who could not use the target control reads the control's own blocked
 * sentence instead of a dead button.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowUpRight } from 'lucide-react';

import type { DropGenPlanDetail } from '@trafficpulse/shared';

import { useAuth } from '@/providers/AuthProvider';

import type { ChapterBridge } from '../tutorial-chapters';

export type BridgeParams = Record<string, string | null>;

/** URL params for a bridge; the editor opens the plan's first container. */
export function resolveBridge(
  bridge: ChapterBridge,
  plan: DropGenPlanDetail,
): BridgeParams {
  if (bridge.editor) {
    const first = plan.containers[0]?.id ?? 'new';
    return { container: first, etab: bridge.editor };
  }
  return { tab: bridge.tab === 'board' || !bridge.tab ? null : bridge.tab };
}

const BLOCKED: Record<NonNullable<ChapterBridge['requires']>, string> = {
  'drop_generator.generate': 'You need the generate permission for this.',
  'drop_generator.manage': 'You need the manage permission to change the plan.',
};

export const TutorialBridge: FC<{
  bridge: ChapterBridge;
  plan: DropGenPlanDetail;
  onBridge: (params: BridgeParams) => void;
}> = ({ bridge, plan, onBridge }) => {
  const { hasPermission } = useAuth();
  if (bridge.requires && !hasPermission(bridge.requires))
    return (
      <span className="text-xs text-muted-foreground">{BLOCKED[bridge.requires]}</span>
    );
  return (
    <button
      type="button"
      onClick={() => onBridge(resolveBridge(bridge, plan))}
      className="inline-flex items-center gap-1 rounded-md border border-input px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
    >
      Show me on my plan — {bridge.label} <ArrowUpRight className="h-3 w-3" />
    </button>
  );
};
